import React, { useState, useRef, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useDocumentTitle } from "@shared/hooks/useDocumentTitle";
import { Title, Text, Stack, Container, Paper, Button } from "@shared/ui";
import HousienLogo from "@components/business/auth/HousienLogo";

const decodeHtmlEntities = (text: string): string => {
  const textarea = document.createElement('textarea');
  textarea.innerHTML = text;
  return textarea.value;
};

const ANTI_BOT_TITLE_PATTERNS = ["anti-bot", "antibot", "InvalidToken", "TokenError"];
const ANTI_BOT_CODE_PATTERNS = ["RECAPTCHA_ERROR", "ANTI_BOT_ERROR", "ANTI_BOT"];

const ERROR_MESSAGE_TRANSLATIONS: Array<{ pattern: (msg: string) => boolean; key: string }> = [
  { pattern: (m) => m.includes("anti-bot token is required") && m.includes("start_authentication"), key: "error.recaptcha.tokenRequired" },
  { pattern: (m) => m.includes("anti-bot token is required") && m.includes("not available"), key: "error.recaptcha.tokenNotAvailable" },
  { pattern: (m) => m.includes("recaptcha") && m.includes("not ready"), key: "error.recaptcha.notReady" },
];

const matchesAntiBotTitle = (title: string): boolean => {
  const lowerTitle = title.toLowerCase();
  return ANTI_BOT_TITLE_PATTERNS.some(pattern =>
    lowerTitle.includes(pattern.toLowerCase()) || title.includes(pattern)
  );
};

const matchesAntiBotCode = (code: string): boolean =>
  ANTI_BOT_CODE_PATTERNS.some(pattern => code === pattern || code.includes(pattern));

const translateErrorMessage = (message: string | undefined, t: (key: string) => string): string => {
  if (!message) return "";
  const lowerMessage = message.toLowerCase();
  const match = ERROR_MESSAGE_TRANSLATIONS.find(entry => entry.pattern(lowerMessage));
  return match ? t(match.key) : message;
};

const getRecaptchaTranslation = (errorTitle: string | undefined, t: (key: string, opts?: any) => string): string => {
  if (!errorTitle) return t("error.recaptcha.details");
  const translationKey = `error.antiBot.${errorTitle}`;
  const translated = t(translationKey);
  return (translated && translated !== translationKey) ? translated : t("error.recaptcha.details");
};

const CTA_KEYS: Record<string, string> = {
  oauth: "error.oauth.cta",
  timeout: "error.timeout.cta",
  route: "error.route.cta",
  general: "error.general.cta",
  social: "error.social.cta",
};

const ErrorIllustration: React.FC = () => (
  <svg
    viewBox="0 0 200 200"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    className="w-full h-full"
    aria-hidden="true"
  >
    <circle cx="100" cy="100" r="90" fill="var(--surface-secondary)" />
    <path
      d="M100 40 L160 150 H40 Z"
      fill="none"
      stroke="var(--accent-primary)"
      strokeWidth="6"
      strokeLinejoin="round"
    />
    <circle cx="100" cy="125" r="5" fill="var(--accent-primary)" />
    <rect x="97" y="75" width="6" height="35" rx="3" fill="var(--accent-primary)" />
  </svg>
);

type ErrorPageProps = {
  missingArguments?: string[];
  errorType?: "route" | "oauth" | "general" | "timeout" | "social";
  errorCode?: string;
  errorMessage?: string;
  errorTitle?: string;
};

const ErrorDetailsSection: React.FC<{
  details: string | undefined;
  showDetails: boolean;
  onToggle: () => void;
  detailsRef: React.RefObject<HTMLDivElement | null>;
  t: (key: string) => string;
}> = ({ details, showDetails, onToggle, detailsRef, t }) => {
  if (!details) return null;

  const toggleLabel = showDetails ? t("error.details.hide") : t("error.details.show");

  return (
    <div className="w-full">
      <button
        type="button"
        className="text-[var(--text-primary)] p-0 no-underline border-0 bg-transparent text-sm font-medium inline-flex items-center hover:opacity-70 transition-opacity duration-150 cursor-pointer"
        onClick={onToggle}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            onToggle();
          }
        }}
        aria-expanded={showDetails}
        aria-controls="error-details"
        aria-label={toggleLabel}
      >
        <span className={`mr-2 transition-transform duration-200 ${showDetails ? 'rotate-180' : ''}`}>
          &#9662;
        </span>
        {toggleLabel}
      </button>

      <div
        ref={detailsRef as unknown as React.LegacyRef<HTMLDivElement>}
        id="error-details"
        className={`overflow-hidden transition-all duration-300 ease-in-out ${showDetails ? 'max-h-96 opacity-100 mt-3' : 'max-h-0 opacity-0'}`}
      >
        <Paper padding="sm" radius="sm" className="bg-[var(--surface-secondary)] border border-[var(--border-subtle)]">
          <Text size="sm" className="block">
            {decodeHtmlEntities(details).split('\n').map((line, index, array) => (
              <React.Fragment key={index}>
                {line}
                {index < array.length - 1 && <br />}
              </React.Fragment>
            ))}
          </Text>
        </Paper>
      </div>
    </div>
  );
};

const ErrorPage: React.FC<ErrorPageProps> = ({
  missingArguments = [],
  errorType = "route",
  errorCode,
  errorMessage,
  errorTitle
}) => {
  const { t } = useTranslation();
  useDocumentTitle(t("error.pageTitle"));
  const location = useLocation();
  const navigate = useNavigate();
  const [showDetails, setShowDetails] = useState(false);
  const detailsRef = useRef<HTMLDivElement | null>(null);

  const isRecaptchaError = (title?: string, code?: string): boolean => {
    if (title) return matchesAntiBotTitle(title);
    if (code) return matchesAntiBotCode(code);
    return false;
  };

  const getErrorCodeDetails = (): string | null => {
    if (!errorCode) return null;
    const recaptcha = isRecaptchaError(errorTitle, errorCode);
    const noMessageFallback = recaptcha ? t("error.recaptcha.details") : t("error.oauth.unexpected");
    if (!errorMessage) return noMessageFallback;
    return recaptcha
      ? getRecaptchaTranslation(errorTitle, t)
      : t("error.oauth.providerError", { code: errorCode, message: translateErrorMessage(errorMessage, t) });
  };

  const getOAuthErrorDetails = (): string => {
    const codeDetails = getErrorCodeDetails();
    if (codeDetails) return codeDetails;

    if (missingArguments.length > 0) {
      return t("error.oauth.missing", { params: missingArguments.join(", ") });
    }

    return t("error.oauth.unexpected");
  };

  const ERROR_MESSAGE_BUILDERS: Record<string, () => { title: string; message: string; details: string | undefined }> = {
    oauth: () => ({
      title: t("error.oauth.title"),
      message: t("error.oauth.message"),
      details: getOAuthErrorDetails()
    }),
    timeout: () => ({
      title: t("error.timeout.title"),
      message: t("error.timeout.message"),
      details: t("error.timeout.details")
    }),
    route: () => ({
      title: t("error.route.title"),
      message: t("error.route.message"),
      details: t("error.route.path", { path: location.pathname })
    }),
    social: () => ({
      title: t("error.social.title"),
      message: t("error.social.message"),
      details: t("error.social.details")
    }),
  };

  const getDefaultErrorMessage = () => ({
    title: t("error.general.title"),
    message: t("error.general.message"),
    details: missingArguments.length > 0
      ? t("error.general.missing", { params: missingArguments.join(", ") })
      : undefined
  });

  const getErrorMessage = () => {
    const builder = ERROR_MESSAGE_BUILDERS[errorType];
    return builder ? builder() : getDefaultErrorMessage();
  };

  const errorInfo = getErrorMessage();
  const ctaKey = CTA_KEYS[errorType] ?? CTA_KEYS.general;
  const ctaLabel = t(ctaKey);

  const handleCta = () => {
    if (errorType === "social") {
      navigate("/login/identity");
    } else if (errorType === "timeout" || errorType === "oauth") {
      window.location.reload();
    } else {
      navigate(-1);
    }
  };

  useEffect(() => {
    if (showDetails && detailsRef.current) {
      const timer = setTimeout(() => {
        detailsRef.current?.scrollIntoView({
          behavior: 'smooth',
          block: 'nearest',
          inline: 'nearest'
        });
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [showDetails]);

  return (
    <div className="min-h-screen w-full bg-[var(--surface-primary)] flex flex-col overflow-x-hidden">
      <div style={{ height: "72px", padding: "0 16px" }} className="flex items-center">
        <HousienLogo className="logo-clip-offset-md" />
      </div>

      <div className="flex-1 flex items-center justify-center">
        <Container size="lg" className="py-8 md:py-12 lg:py-0 w-full">
          <div className="flex flex-col lg:flex-row items-center lg:items-center gap-8 lg:gap-16">
            {/* Text content -- always first visually */}
            <div className="w-full lg:w-7/12 order-1">
              <Stack gap="lg">
                <Title order={1} className="text-2xl md:text-3xl lg:text-4xl">
                  {errorInfo.title}
                </Title>

                <Text size="lg" className="leading-relaxed text-[var(--text-secondary)]">
                  {errorInfo.message}
                </Text>

                <ErrorDetailsSection
                  details={errorInfo.details}
                  showDetails={showDetails}
                  onToggle={() => setShowDetails(!showDetails)}
                  detailsRef={detailsRef}
                  t={t}
                />

                <div className="flex flex-col sm:flex-row items-start gap-4">
                  <Button
                    variant="default"
                    size="lg"
                    onClick={handleCta}
                  >
                    {ctaLabel}
                  </Button>
                </div>

                <Text size="sm" className="text-[var(--text-tertiary)]">
                  {t("error.help")}
                </Text>
              </Stack>
            </div>

            {/* Illustration -- constrained, theme-aware */}
            <div className="w-full lg:w-5/12 order-2 flex justify-center">
              <div className="w-48 h-48 md:w-56 md:h-56 lg:w-72 lg:h-72">
                <ErrorIllustration />
              </div>
            </div>
          </div>
        </Container>
      </div>
    </div>
  );
};

export default ErrorPage;
